import { FC, useState } from 'react';
import { TableBody as Body } from '@mui/material';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';

import { CustomTableRow, RowTableCell } from '../../../../../shared/uiComponents/Table/tableHelpers';
import { CustomAlert, CustomProgressBar, CustomRadio, TableNoData, Text } from '../../../../../shared/uiComponents';
import { HeaderProperties } from '../../../../../shared/uiComponents/Table/tableProperties';
import { GREEN, ORANGE, PRIMARY, RED } from '../../../../../shared/Helpers/colors';
import { InfoRow } from '../../../../../components/StyledComponents';
import { ExpireInfo } from '../../../../Dashboard/helpers';
import { InfoFilled } from '../../../../../shared/Assets/svgIcons';

import {
	AllocationPageAuthorizationProperties,
	ProviderUtilizationProperties,
	TherapistAllocationProperties,
} from '../../../../../redux/API/ClientAPIHelpers/insuranceAuthorizationProperties';
import './styles.scss';

interface RowRendererProperties {
	data: AllocationPageAuthorizationProperties[];
}

const getUtilizationColor = (utilizationPercentage: number): string => {
	if (utilizationPercentage <= 70) return GREEN.BASE;
	if (utilizationPercentage <= 90) return ORANGE.BASE;

	return RED.BASE;
};

const getRemainingHoursColor = (remainingHours: number, totalHours: number): string => {
	if (totalHours === 0) return GREEN.BASE;
	const remainingPercentage = (remainingHours / totalHours) * 100;
	if (remainingPercentage > 20) return GREEN.BASE;
	if (remainingPercentage >= 10) return ORANGE.BASE;

	return RED.BASE;
};

export const headers: HeaderProperties[] = [
	{ id: '0', name: '', width: '40px' },
	{ id: '1', name: 'Session' },
	{ id: '2', name: 'Total' },
	{ id: '3', name: 'Weekly' },
	{ id: '4', name: 'Billed', width: '50px' },
	{
		id: '8',
		name: 'Remaining',
		tooltip: (
			<div>
				<div>Hours still available to be billed.</div>
				<div style={{ marginTop: 4 }}>
					<span style={{ color: GREEN.BASE }}>● Green:</span> {'>20%'}
				</div>
				<div>
					<span style={{ color: ORANGE.BASE }}>● Orange:</span> 10-20%
				</div>
				<div>
					<span style={{ color: RED.BASE }}>● Red:</span> {'<10% or negative'}
				</div>
			</div>
		),
	},
	{
		id: '5',
		name: 'Utilization %',
		width: '120px',
		tooltip: (
			<div>
				<div>Billed vs authorized hours.</div>
				<div style={{ marginTop: 4 }}>
					<span style={{ color: GREEN.BASE }}>● Green:</span> 0-70%
				</div>
				<div>
					<span style={{ color: ORANGE.BASE }}>● Orange:</span> 71-90%
				</div>
				<div>
					<span style={{ color: RED.BASE }}>● Red:</span> 91%+ (over capacity)
				</div>
			</div>
		),
	},
	{ id: '7', name: 'Allocated' },
	{
		id: '6',
		name: 'Available',
		tooltip: 'Hours still available to be allocated.',
	},
	{ id: '9', name: 'Type' },
	{ id: '10', name: 'Date' },
	{
		id: '11',
		name: 'Status',
		tooltip: (
			<div>
				<div>
					<span style={{ color: GREEN.BASE }}>● Active:</span> {'>30 days remaining'}
				</div>
				<div>
					<span style={{ color: ORANGE.BASE }}>● Warning:</span> expires within 30 days
				</div>
				<div>
					<span style={{ color: RED.BASE }}>● Expired:</span> period ended
				</div>
			</div>
		),
	},
];

const UsedHoursPopup = ({ usedHours, providerUtilization }: UsedHoursPopupProperties) => {
	const [open, setOpen] = useState<boolean>(false);

	return (
		<>
			<Text
				title={`${usedHours} h`}
				type="psm"
				style={{
					textDecoration: 'underline',
					color: PRIMARY.LIGHT[16],
					cursor: 'pointer',
					width: '50px',
				}}
				onClick={() => setOpen(true)}
			/>
			<CustomAlert
				open={open}
				onClose={() => setOpen(false)}
				title="Used Hours Details"
				Content={() =>
					providerUtilization.length ? (
						<>
							<InfoRow>
								<Text title="Provider" type="psm" fontWeight="semiBold" />
								<Text title="Used / Allocated" type="psm" />
							</InfoRow>
							{providerUtilization.map((utilization, index) => (
								<InfoRow key={utilization.provider.id}>
									<Text title={`${index + 1}. ${utilization.provider.fullName}`} type="psm" fontWeight="semiBold" />
									<Text
										title={`${utilization.usedHours} h${
											utilization.allocatedHours === null ? '' : ` / ${utilization.allocatedHours} h`
										}`}
										type="psm"
									/>
								</InfoRow>
							))}
						</>
					) : (
						<Text title="No usage data" type="psm" />
					)
				}
			/>
		</>
	);
};

const AllocatedHoursPopup = ({ allocatedDisplay, therapistAllocations }: AllocatedHoursPopupProperties) => {
	const [open, setOpen] = useState<boolean>(false);

	return (
		<>
			<Text
				title={allocatedDisplay}
				type="psm"
				className="allocatedLink"
				color={PRIMARY.LIGHT[16]}
				onClick={() => setOpen(true)}
			/>
			<CustomAlert
				open={open}
				onClose={() => setOpen(false)}
				title="Allocated Hours Details"
				Content={() =>
					therapistAllocations.length ? (
						<>
							<InfoRow>
								<Text title="Name" type="psm" fontWeight="semiBold" />
								<Text title="Frequency - Hours" type="psm" />
							</InfoRow>
							{therapistAllocations.map((allocation, index) => (
								<InfoRow key={allocation.user.id}>
									<Text title={`${index + 1}. ${allocation.user.fullName}`} type="psm" fontWeight="semiBold" />
									<Text
										title={`${allocation.frequency.name} - ${
											allocation.hoursPerWeek === null ? allocation.totalHours : allocation.hoursPerWeek
										}`}
										type="psm"
									/>
								</InfoRow>
							))}
						</>
					) : (
						<Text title="No allocations" type="psm" />
					)
				}
			/>
		</>
	);
};

export const TableBody: FC<RowRendererProperties> = ({ data }) => {
	const [params, setParams] = useSearchParams();

	const selectDetailsHandler = (detailId: string, authorizationId: string) => {
		params.set('detailsId', detailId);
		params.set('authId', authorizationId);
		setParams(params);
	};

	if (!data?.length) {
		return <TableNoData spanScope={headers.length} />;
	}

	return (
		<Body>
			{data.map((row, index) => {
				const detailsCount = row.details.length;

				return row.details.map((detail, detailIndex) => {
					const utilization = detail.utilizationPercentage ?? 0;
					const remainingHours = detail.remainingHours;
					const totalHours = detail.totalHours;
					const usedHours = detail.usedHours;
					const isOverUtilized = utilization > 100;
					const isNegativeRemaining = remainingHours < 0;
					const remainingColor = getRemainingHoursColor(remainingHours, totalHours);

					// Determine available hours display based on frequency
					const isWeekly = detail.frequency.frequency === 1;
					const availableHours = isWeekly
						? detail.availableHoursPerWeek === null
							? '-'
							: `${detail.availableHoursPerWeek} h/week`
						: `${detail.availableTotalHours} h`;

					// Determine allocated hours display
					const allocatedDisplay = isWeekly
						? detail.allocatedHoursPerWeek === null
							? `${detail.allocatedTotalHours} h`
							: `${detail.allocatedHoursPerWeek} h/week`
						: `${detail.allocatedTotalHours} h`;

					return (
						<CustomTableRow key={`${index}-${detailIndex}`} className="insuranceTableRow">
							<RowTableCell style={{ width: '40px', padding: '12px 8px' }}>
								<CustomRadio
									onChange={() => selectDetailsHandler(detail.id, row.id)}
									id={detail.id}
									value={params.get('detailsId')}
								/>
							</RowTableCell>
							<RowTableCell>
								<Text title={detail.sessionType.name} type="psm" />
							</RowTableCell>
							<RowTableCell>{detail.totalHours}</RowTableCell>
							<RowTableCell>{detail.hoursPerWeek === null ? '-' : detail.hoursPerWeek}</RowTableCell>

							{/* Billed/Used Hours */}
							<RowTableCell>
								<UsedHoursPopup usedHours={usedHours} providerUtilization={detail.providerUtilization} />
							</RowTableCell>

							{/* Remaining Hours */}
							<RowTableCell>
								<div className="remainingHoursWrapper">
									<Text
										title={`${remainingHours} h`}
										type="psm"
										color={remainingColor}
										fontWeight={isNegativeRemaining ? 'bold' : 'regular'}
									/>
									{isNegativeRemaining && <InfoFilled color={RED.BASE} />}
								</div>
							</RowTableCell>

							{/* Utilization % */}
							<RowTableCell>
								<CustomProgressBar
									percentage={utilization}
									color={getUtilizationColor(utilization)}
									icon={isOverUtilized ? <InfoFilled color={RED.BASE} /> : undefined}
								/>
							</RowTableCell>

							{/* Allocated Hours */}
							<RowTableCell>
								<AllocatedHoursPopup
									allocatedDisplay={allocatedDisplay}
									therapistAllocations={detail.therapistAllocations}
								/>
							</RowTableCell>

							{/* Available Hours */}
							<RowTableCell>
								<Text title={availableHours} type="psm" />
							</RowTableCell>

							{detailIndex === 0 && (
								<>
									<RowTableCell rowSpan={detailsCount}>
										<Text title={row.type.name} type="psm" fontWeight="medium" />
									</RowTableCell>
									<RowTableCell rowSpan={detailsCount}>
										<Text
											title={`${dayjs(row.startDate).format('MM/DD/YYYY')} - ${dayjs(row.endDate).format(
												'MM/DD/YYYY',
											)}`}
											type="psm"
										/>
									</RowTableCell>
									<RowTableCell rowSpan={detailsCount}>
										<ExpireInfo endDate={row.endDate} />
									</RowTableCell>
								</>
							)}
						</CustomTableRow>
					);
				});
			})}
		</Body>
	);
};

interface AllocatedHoursPopupProperties {
	allocatedDisplay: string;
	therapistAllocations: TherapistAllocationProperties[];
}

interface UsedHoursPopupProperties {
	providerUtilization: ProviderUtilizationProperties[];

	usedHours: number;
}
